const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MIN_WIDTH = 200;
const MIN_HEIGHT = 200;
const MAX_WIDTH = 10000;
const MAX_HEIGHT = 10000;
const MAX_ASPECT_RATIO = 5; // width/height or height/width

export const validateImageFile = async (file) => {
  const errors = [];

  if (!file) {
    return { valid: false, error: "No file provided" };
  }

  if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      error: `Invalid file type. Please upload a JPG, PNG, or WEBP image.`,
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size too large. Maximum size is ${
        MAX_FILE_SIZE / 1024 / 1024
      }MB.`,
    };
  }

  try {
    const dimensions = await getImageDimensions(file);

    if (dimensions.width < MIN_WIDTH || dimensions.height < MIN_HEIGHT) {
      errors.push(
        `Image too small. Minimum dimensions: ${MIN_WIDTH}x${MIN_HEIGHT}px.`
      );
    }

    if (dimensions.width > MAX_WIDTH || dimensions.height > MAX_HEIGHT) {
      errors.push(
        `Image too large. Maximum dimensions: ${MAX_WIDTH}x${MAX_HEIGHT}px.`
      );
    }

    const aspectRatio = dimensions.width / dimensions.height;
    const inverseAspectRatio = dimensions.height / dimensions.width;

    if (
      aspectRatio > MAX_ASPECT_RATIO ||
      inverseAspectRatio > MAX_ASPECT_RATIO
    ) {
      errors.push(
        `Image aspect ratio too extreme. Please use images with a more balanced width-to-height ratio.`
      );
    }

    if (errors.length > 0) {
      return { valid: false, error: errors.join(" ") };
    }

    return { valid: true, dimensions };
  } catch (error) {
    return {
      valid: false,
      error: "Unable to read image file. Please ensure it's a valid image.",
    };
  }
};

const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
};

export const validateImageContent = async (file) => {
  try {
    const dimensions = await getImageDimensions(file);

    const isLikelyDentalImage = checkDentalImageHeuristics(dimensions);

    if (!isLikelyDentalImage) {
      return {
        valid: false,
        warning:
          "This image may not be suitable for dental analysis. Please ensure you're uploading an intraoral or dental X-ray image.",
      };
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      warning: "Could not validate image content.",
    };
  }
};

const checkDentalImageHeuristics = (dimensions) => {
  const { width, height } = dimensions;
  const aspectRatio = width / height;

  const area = width * height;
  const megapixels = area / (1000 * 1000);

  const isSquareish = aspectRatio >= 0.7 && aspectRatio <= 1.4;
  const isPortrait = aspectRatio >= 0.4 && aspectRatio <= 0.7;
  const isLandscape = aspectRatio >= 1.4 && aspectRatio <= 2.5;

  const reasonableAspectRatio = isSquareish || isPortrait || isLandscape;
  const reasonableSize = megapixels >= 0.1 && megapixels <= 50;

  return reasonableAspectRatio && reasonableSize;
};
